import { Coordinate, FuelTransaction, FuelUsed, StatusData } from "@fairfleet/geotab";
import { RefuelEntry } from "../models/AuditTrip";
import { ElectricEnergyReadings } from "@/hooks/statusData/useElectricEnergyForDevices";

interface Window {
  start: Date;
  stop: Date;
}

function isInWindow(dateTime: Date, window: Window) {
  const time = dateTime.getTime();
  return time >= window.start.getTime() && time <= window.stop.getTime();
}

/**
 * Litres used during the trip: Geotab stamps one FuelUsed event per trip at its end. The
 * window is strict; a tolerance would count the event of a back-to-back neighbour twice.
 */
export function fuelUsedForTrip(trip: Window, records: FuelUsed[] = []): number | undefined {
  let total: number | undefined;
  for (const record of records) {
    if (!isInWindow(record.dateTime, trip)) continue;
    if (typeof record.totalFuelUsed !== "number" || !Number.isFinite(record.totalFuelUsed)) continue;
    total = (total ?? 0) + record.totalFuelUsed;
  }
  return total;
}

/** Difference between the last and first reading of a cumulative counter inside the window. */
export function counterDelta(window: Window, readings: StatusData[]): number | undefined {
  let first: StatusData | undefined;
  let last: StatusData | undefined;
  for (const reading of readings) {
    if (!isInWindow(reading.dateTime, window)) continue;
    if (typeof reading.data !== "number" || !Number.isFinite(reading.data)) continue;
    if (!first || reading.dateTime < first.dateTime) first = reading;
    if (!last || reading.dateTime > last.dateTime) last = reading;
  }
  if (!first || !last || first === last) return undefined;
  const delta = last.data - first.data;
  // A cumulative counter never decreases; a drop means the device reset inside the window.
  return delta >= 0 ? delta : undefined;
}

/**
 * Whole-day consumption of a vehicle over a calendar day (`yyyy-MM-dd` in the audit's zone):
 * the FuelUsed events of the day, the energy delta for electric vehicles.
 */
export type DayConsumptionLookup = (deviceId: string, dateKey: string) => number | undefined;

/**
 * Litres of a combustion vehicle inside a window: the sum of Geotab's FuelUsed events, the
 * figure of Geotab's Kraftstoff- und Stromverbrauchsbericht. The events are derived from the
 * engine's total-fuel counter and add up to too little when a vehicle reports that counter only
 * every hour or so, but the audit has to show the report's numbers (Jan, 05.10.2026), so the
 * counter is not consulted.
 */
export function fuelForWindow(window: Window, events: FuelUsed[] = []): number | undefined {
  return fuelUsedForTrip(window, events);
}

/**
 * kWh consumed inside a window: energy taken out of the battery minus energy recuperated
 * into it, the same figure Geotab's fuel and EV report calls Stromverbrauch. Needs at least
 * two "energy out" readings; missing "energy in" readings count as zero recuperation.
 */
export function electricEnergyForWindow(
  window: Window,
  readings: ElectricEnergyReadings | undefined,
): number | undefined {
  if (!readings) return undefined;
  const out = counterDelta(window, readings.out);
  if (out == null) return undefined;
  const hasInReadings = readings.in.some(reading => isInWindow(reading.dateTime, window));
  const recuperated = hasInReadings ? counterDelta(window, readings.in) : 0;
  if (recuperated == null) return undefined;
  const used = out - recuperated;
  return used >= 0 ? used : undefined;
}

/**
 * The app records a fuel-up either during the trip or in the end-of-trip dialog, which the
 * driver fills in after Geotab has already registered the stop. A refuelling therefore counts
 * for a trip until the next trip starts, at most this long after the stop.
 */
export const REFUEL_TAIL_MS = 60 * 60 * 1000;

/**
 * A consumption of 0 over a distance greater than 0 is a missing signal, not a measurement:
 * Geotab derives FuelUsed from the engine's total-fuel counter, which some vehicles report only
 * a few times a day, so short trips get 0 l and their litres land on the neighbouring trip.
 * 0 over 0 km stays 0 (the vehicle did not move).
 */
export function measuredConsumption(value: number | undefined, distanceKm: number | undefined): number | undefined {
  if (value === 0 && distanceKm != null && distanceKm > 0) return undefined;
  return value;
}

/** Geotab's product type for AdBlue. */
const AD_BLUE_PRODUCT_TYPE = "DieselExhaustFluid";
/** The EcoApp stamps every transaction it writes with this reference prefix (fuelService.ts). */
const APP_REFERENCE_PREFIX = "APP-";

export type RefuelKind = "fuel" | "adBlue";

/**
 * Fuel transactions of the trip: from the trip start until the next trip start, capped at
 * `REFUEL_TAIL_MS` after the stop. Fuel and AdBlue are told apart like the paper logbook's
 * Tankungen columns; the source says whether the driver typed it into the EcoApp or it was
 * imported (fuel card). Geotab's own fill-up detection is not part of this.
 */
export function refuelsForTrip(
  trip: Window,
  transactions: FuelTransaction[] = [],
  nextTripStart?: Date,
): RefuelEntry[] {
  const tail = trip.stop.getTime() + REFUEL_TAIL_MS;
  // Strictly before the next entry, even when that entry starts while this trip still runs (a
  // roster confirmed during a Geotab trip): every transaction belongs to exactly one entry.
  const end = nextTripStart ? Math.min(nextTripStart.getTime() - 1, tail) : tail;
  return refuelsInWindow({ start: trip.start, stop: new Date(end) }, transactions);
}

/**
 * The first of `starts` (ascending epoch milliseconds) after `time`: the start of the next entry,
 * digital trip or analog booking, which caps an entry's refuel tail. Without the cap, bookings a
 * few minutes apart each count the same fuel transactions.
 */
export function nextStartAfter(starts: number[], time: Date): Date | undefined {
  let low = 0;
  let high = starts.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (starts[middle] <= time.getTime()) low = middle + 1;
    else high = middle;
  }
  return low < starts.length ? new Date(starts[low]) : undefined;
}

/** A coordinate Geotab filled in; (0, 0) and missing values mean "unknown". */
export function knownLocation(location: unknown): Coordinate | undefined {
  const candidate = location as Partial<Coordinate> | null | undefined;
  if (!candidate || typeof candidate.x !== "number" || typeof candidate.y !== "number") return undefined;
  if (!Number.isFinite(candidate.x) || !Number.isFinite(candidate.y) || (candidate.x === 0 && candidate.y === 0)) return undefined;
  return { x: candidate.x, y: candidate.y };
}

/** The API returns the transaction's position, the client's type does not declare it. */
type LocatedFuelTransaction = FuelTransaction & { location?: unknown };

/** Every fuel transaction stamped inside the window, e.g. all fill-ups of the audited period. */
export function refuelsInWindow(window: Window, transactions: LocatedFuelTransaction[] = []): RefuelEntry[] {
  const entries: RefuelEntry[] = [];
  for (const transaction of transactions) {
    if (!isInWindow(transaction.dateTime, window)) continue;
    if (typeof transaction.volume !== "number" || !Number.isFinite(transaction.volume)) continue;
    const fromApp = String(transaction.externalReference ?? "").startsWith(APP_REFERENCE_PREFIX);
    entries.push({
      dateTime: transaction.dateTime,
      liters: transaction.volume,
      kind: String(transaction.productType) === AD_BLUE_PRODUCT_TYPE ? "adBlue" : "fuel",
      source: fromApp ? "app" : "import",
      provider: fromApp ? undefined : String(transaction.provider ?? "") || undefined,
      location: knownLocation(transaction.location),
    });
  }
  return entries.sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());
}

/** Litres of one kind over the entries; undefined when there is none of that kind. */
export function refuelLiters(entries: RefuelEntry[], kind: RefuelKind): number | undefined {
  let total: number | undefined;
  for (const entry of entries) if (entry.kind === kind) total = (total ?? 0) + entry.liters;
  return total;
}

/** Convenience for callers that only need the litres of one kind for a trip. */
export function refuelLitersForTrip(
  trip: Window,
  transactions: FuelTransaction[] = [],
  nextTripStart?: Date,
  kind: RefuelKind = "fuel",
): number | undefined {
  return refuelLiters(refuelsForTrip(trip, transactions, nextTripStart), kind);
}
